<template>
    <div class="pressure-container">
        <!-- Time Range Tabs -->
        <div class="time-tabs">
            <button
                v-for="range in timeRanges"
                :key="range.value"
                class="time-tab"
                :class="{ active: selectedRange === range.value }"
                @click="selectRange(range.value)"
            >
                {{ range.label }}
            </button>
        </div>

        <!-- Main Pressure Bar -->
        <div class="pressure-bar-wrapper">
            <div class="pressure-labels">
                <span class="label-buy">Buy</span>
                <span class="label-sell">Sell</span>
            </div>
            <div class="pressure-bar">
                <div
                    class="pressure-fill pressure-fill--buy"
                    :style="{ width: buyPercentage + '%' }"
                >
                    <span class="pressure-percent" v-if="buyPercentage >= 15">{{ buyPercentage.toFixed(0) }}%</span>
                </div>
                <div
                    class="pressure-fill pressure-fill--sell"
                    :style="{ width: sellPercentage + '%' }"
                >
                    <span class="pressure-percent" v-if="sellPercentage >= 15">{{ sellPercentage.toFixed(0) }}%</span>
                </div>
            </div>
        </div>

        <!-- Stats Grid -->
        <div class="pressure-stats">
            <div class="stat-item">
                <span class="stat-label">Volume</span>
                <span class="stat-value">{{ formatVolume(pressureData.volume) }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Traders</span>
                <span class="stat-value">{{ formatNumber(pressureData.traders) }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Net Buyers</span>
                <span class="stat-value" :class="netBuyersClass">
                    {{ netBuyersFormatted }}
                </span>
            </div>
        </div>

        <!-- Mini Trend Chart -->
        <div class="trend-chart" v-if="showTrendChart">
            <div class="trend-header">
                <span class="trend-title">Pressure Trend</span>
            </div>
            <div class="trend-bars">
                <div
                    v-for="(point, idx) in trendData"
                    :key="idx"
                    class="trend-bar-group"
                >
                    <div class="trend-bar trend-bar--buy" :style="{ height: point.buyHeight + '%' }"></div>
                    <div class="trend-bar trend-bar--sell" :style="{ height: point.sellHeight + '%' }"></div>
                </div>
            </div>
            <div class="trend-time-axis">
                <span>{{ trendStartLabel }}</span>
                <span>Now</span>
            </div>
        </div>

        <!-- TODO: Wire up real trade analysis data from API when available -->
    </div>
</template>

<script>
export default {
    name: 'BuySellPressure',

    props: {
        mint: {
            type: String,
            required: true
        },
        initialData: {
            type: Object,
            default: null
        },
        showTrendChart: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            hasRealData: false,
            selectedRange: '24h',
            timeRanges: [
                { label: '5m', value: '5m' },
                { label: '1h', value: '1h' },
                { label: '6h', value: '6h' },
                { label: '24h', value: '24h' }
            ],
            // Mock data - structured for real API integration
            pressureDataByRange: {
                '5m': {
                    buyVolume: 125000,
                    sellVolume: 98000,
                    volume: 223000,
                    traders: 156,
                    buyers: 89,
                    sellers: 67
                },
                '1h': {
                    buyVolume: 520000,
                    sellVolume: 445000,
                    volume: 965000,
                    traders: 892,
                    buyers: 498,
                    sellers: 394
                },
                '6h': {
                    buyVolume: 1450000,
                    sellVolume: 1120000,
                    volume: 2570000,
                    traders: 3240,
                    buyers: 1820,
                    sellers: 1420
                },
                '24h': {
                    buyVolume: 1310000,
                    sellVolume: 810000,
                    volume: 2120000,
                    traders: 5460,
                    buyers: 3300,
                    sellers: 2160
                }
            },
            // Mock trend data - 12 data points
            trendDataByRange: {
                '5m': this.generateTrendData(12, 55, 15),
                '1h': this.generateTrendData(12, 52, 20),
                '6h': this.generateTrendData(12, 58, 18),
                '24h': this.generateTrendData(12, 62, 12)
            }
        };
    },

    computed: {
        pressureData() {
            return this.pressureDataByRange[this.selectedRange];
        },

        buyPercentage() {
            const data = this.pressureData;
            const total = data.buyVolume + data.sellVolume;
            return total > 0 ? (data.buyVolume / total) * 100 : 50;
        },

        sellPercentage() {
            return 100 - this.buyPercentage;
        },

        netBuyers() {
            const data = this.pressureData;
            return data.buyers - data.sellers;
        },

        netBuyersFormatted() {
            const net = this.netBuyers;
            const prefix = net >= 0 ? '+' : '';
            return prefix + this.formatNumber(Math.abs(net));
        },

        netBuyersClass() {
            if (this.netBuyers > 0) return 'stat-positive';
            if (this.netBuyers < 0) return 'stat-negative';
            return '';
        },

        trendData() {
            return this.trendDataByRange[this.selectedRange];
        },

        trendStartLabel() {
            const labels = {
                '5m': '5m ago',
                '1h': '1h ago',
                '6h': '6h ago',
                '24h': '24h ago'
            };
            return labels[this.selectedRange];
        }
    },

    methods: {
        selectRange(range) {
            this.selectedRange = range;
            // TODO: Fetch real data when API is available
            // this.fetchPressureData();
        },

        generateTrendData(points, baseBuyPercent, variance) {
            const data = [];
            for (let i = 0; i < points; i++) {
                const buyPercent = baseBuyPercent + (Math.random() - 0.5) * variance;
                const normalizedBuy = Math.max(20, Math.min(80, buyPercent));
                data.push({
                    buyHeight: normalizedBuy,
                    sellHeight: 100 - normalizedBuy
                });
            }
            return data;
        },

        formatVolume(value) {
            if (value >= 1000000) {
                return '$' + (value / 1000000).toFixed(2) + 'M';
            }
            if (value >= 1000) {
                return '$' + (value / 1000).toFixed(1) + 'K';
            }
            return '$' + value.toFixed(0);
        },

        formatNumber(value) {
            if (value >= 1000) {
                return (value / 1000).toFixed(2) + 'K';
            }
            return value.toLocaleString();
        },

        async fetchPressureData() {
            try {
                const response = await fetch(`/api/tokens/${this.mint}/pressure?range=${this.selectedRange}`);
                if (response.ok) {
                    const data = await response.json();
                    this.pressureDataByRange[this.selectedRange] = data;
                    this.hasRealData = true;
                }
            } catch (error) {
                console.error('Failed to fetch pressure data:', error);
            }
        }
    }
};
</script>

<style scoped>
.pressure-container {
    background: var(--compass-bg-secondary, #1a1a2e);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    border-radius: var(--compass-radius, 12px);
    padding: 0.75rem;
}

@media (max-width: 768px) {
    .pressure-container {
        padding: 0.5rem;
    }
}

/* Time Range Tabs */
.time-tabs {
    display: flex;
    gap: 0.15rem;
    margin-bottom: 0.5rem;
    background: rgba(139, 92, 246, 0.08);
    border-radius: 6px;
    padding: 0.15rem;
}

.time-tab {
    flex: 1;
    padding: 0.3rem 0.5rem;
    border: none;
    background: transparent;
    color: var(--compass-text-secondary, #7d8590);
    font-size: 0.7rem;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.time-tab:hover {
    color: var(--compass-text-primary, #e6edf3);
    background: rgba(139, 92, 246, 0.1);
}

.time-tab.active {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    color: #ffffff;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Pressure Bar */
.pressure-bar-wrapper {
    margin-bottom: 0.5rem;
}

.pressure-labels {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.25rem;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.label-buy {
    color: var(--compass-success, #22c55e);
}

.label-sell {
    color: var(--compass-danger, #ef4444);
}

.pressure-bar {
    display: flex;
    height: 24px;
    border-radius: 6px;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
    .pressure-bar {
        height: 20px;
    }
}

.pressure-fill {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
}

.pressure-fill--buy {
    background: linear-gradient(90deg, #16a34a, #22c55e);
}

.pressure-fill--sell {
    background: linear-gradient(90deg, #ef4444, #dc2626);
}

.pressure-percent {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.85rem;
    font-weight: 700;
    color: white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Stats - inline row */
.pressure-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.25rem 0 0;
}

.stat-item {
    display: flex;
    align-items: baseline;
    gap: 0.3rem;
}

.stat-label {
    font-size: 0.6rem;
    color: var(--compass-text-secondary, #7d8590);
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.stat-value {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
}

@media (max-width: 768px) {
    .stat-label {
        font-size: 0.55rem;
    }
    .stat-value {
        font-size: 0.7rem;
    }
}

.stat-positive {
    color: var(--compass-success, #22c55e);
}

.stat-negative {
    color: var(--compass-danger, #ef4444);
}

/* Trend Chart - Hidden on mobile */
.trend-chart {
    margin-top: 0.5rem;
    padding-top: 0.4rem;
}

@media (max-width: 768px) {
    .trend-chart {
        display: none;
    }
}

.trend-header {
    margin-bottom: 0.25rem;
}

.trend-title {
    font-size: 0.65rem;
    color: var(--compass-text-secondary, #7d8590);
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.trend-bars {
    display: flex;
    gap: 2px;
    height: 32px;
    align-items: flex-end;
}

.trend-bar-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 1px;
}

.trend-bar {
    border-radius: 2px;
    transition: height 0.3s ease;
}

.trend-bar--buy {
    background: linear-gradient(180deg, #22c55e, #16a34a);
    order: 1;
}

.trend-bar--sell {
    background: linear-gradient(180deg, #ef4444, #dc2626);
    order: 2;
}

.trend-time-axis {
    display: flex;
    justify-content: space-between;
    margin-top: 0.35rem;
    font-size: 0.65rem;
    color: var(--compass-text-secondary, #7d8590);
}

/* Light mode support - ONLY use body selector since dark-mode class is on body */
body:not(.dark-mode) .pressure-container {
    background: #ffffff;
    border-color: rgba(139, 92, 246, 0.15);
}

body:not(.dark-mode) .stat-value {
    color: #1f2937;
}

body:not(.dark-mode) .stat-label,
body:not(.dark-mode) .trend-title,
body:not(.dark-mode) .trend-time-axis {
    color: #6b7280;
}

body:not(.dark-mode) .time-tab {
    color: #6b7280;
}

body:not(.dark-mode) .time-tab:hover:not(.active) {
    color: #374151;
}

/* IMPORTANT: Preserve white text on active gradient tabs in light mode */
body:not(.dark-mode) .time-tab.active {
    color: #ffffff;
}

body:not(.dark-mode) .time-tabs {
    background: rgba(139, 92, 246, 0.05);
}

body:not(.dark-mode) .pressure-bar {
    background: rgba(0, 0, 0, 0.08);
}
</style>
